import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, ChevronDown, Search, UserRound, MapPinned, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover } from "@/components/ui/popover";
import { PlayersServices } from "@/services/bedrock";
import { useDebounced, useSegmentation } from "@/utils";
import { cn } from "@/lib/utils";

type ScopeTab = "player" | "segmentation";

const SEGMENT_TYPES = ["REGION", "BRANCH"] as const;

function ScopeTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-0 flex-1 cursor-pointer rounded-md px-3 py-1.5 text-center text-xs font-medium transition-all",
        active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-9 bg-background pl-8"
      />
    </div>
  );
}

export function OverviewScopePickerContent({
  enabled,
  playerId,
  segmentationDetailId,
  onSelectPlayer,
  onSelectSegmentation,
  allowedSegmentationIds,
}: {
  enabled: boolean;
  playerId: number | null;
  segmentationDetailId: number | null;
  onSelectPlayer: (playerId: number) => void;
  onSelectSegmentation: (segmentationDetailId: number) => void;
  allowedSegmentationIds?: Set<number> | null;
}) {
  const [playerSearch, setPlayerSearch] = React.useState("");
  const [segmentSearch, setSegmentSearch] = React.useState("");
  const debouncedPlayerSearch = useDebounced(playerSearch.trim(), 300);
  const { segmentationOptions: allSegmentationOptions } = useSegmentation([...SEGMENT_TYPES]);
  const segmentationOptions = React.useMemo(
    () =>
      allowedSegmentationIds
        ? allSegmentationOptions.filter(option => allowedSegmentationIds.has(Number(option.value)))
        : allSegmentationOptions,
    [allSegmentationOptions, allowedSegmentationIds]
  );

  const initialTab: ScopeTab = segmentationDetailId != null && playerId == null ? "segmentation" : "player";
  const [tab, setTab] = React.useState<ScopeTab>(initialTab);

  React.useEffect(() => {
    if (!enabled) {
      setPlayerSearch("");
      setSegmentSearch("");
      return;
    }
    setTab(segmentationDetailId != null && playerId == null ? "segmentation" : "player");
  }, [enabled, playerId, segmentationDetailId]);

  const { data: playerPage, isFetching: isFetchingPlayers } = useQuery({
    queryKey: ["overview-scope-players", debouncedPlayerSearch],
    queryFn: () =>
      PlayersServices.getPlayers({
        search: debouncedPlayerSearch || undefined,
        page: 1,
        perPage: 25,
      }),
    enabled: enabled && tab === "player",
  });

  const playerOptions = React.useMemo(
    () =>
      (playerPage?.content ?? []).map(player => ({
        id: player.id,
        name: player.fullName || "No Name",
        email: player.email,
      })),
    [playerPage?.content]
  );

  const filteredSegments = React.useMemo(() => {
    const query = segmentSearch.trim().toLowerCase();
    if (!query) return segmentationOptions;
    return segmentationOptions.filter(
      option => option.text.toLowerCase().includes(query) || option.label.toLowerCase().includes(query)
    );
  }, [segmentationOptions, segmentSearch]);

  return (
    <>
      <div className="flex w-full rounded-lg bg-muted p-1">
        <ScopeTabButton active={tab === "player"} onClick={() => setTab("player")}>
          Player
        </ScopeTabButton>
        <ScopeTabButton active={tab === "segmentation"} onClick={() => setTab("segmentation")}>
          Branch / Region
        </ScopeTabButton>
      </div>

      {tab === "player" ? (
        <div className="flex flex-col gap-1.5">
          <SearchField value={playerSearch} onChange={setPlayerSearch} placeholder="Search Players..." />
          <div className="max-h-52 overflow-y-auto rounded-lg border border-border/70">
            {playerOptions.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                {isFetchingPlayers
                  ? "Loading Players..."
                  : debouncedPlayerSearch
                  ? "No matching Players."
                  : "No Players found."}
              </p>
            ) : (
              playerOptions.map(player => {
                const selected = player.id === playerId;
                return (
                  <button
                    key={player.id}
                    type="button"
                    onClick={() => onSelectPlayer(player.id)}
                    className={cn(
                      "flex w-full cursor-pointer flex-col gap-0.5 border-b border-border/40 px-3 py-2 text-left last:border-b-0",
                      "hover:bg-muted/60",
                      selected && "bg-muted/50"
                    )}
                  >
                    <span className={cn("truncate text-sm", selected && "font-medium")}>{player.name}</span>
                    <span className="truncate text-xs text-muted-foreground">{player.email}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <SearchField value={segmentSearch} onChange={setSegmentSearch} placeholder="Search segments..." />
          <div className="max-h-52 overflow-y-auto rounded-lg border border-border/70">
            {filteredSegments.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                {segmentationOptions.length === 0 ? "No branches or regions found." : "No matching segments."}
              </p>
            ) : (
              filteredSegments.map(option => {
                const selected = Number(option.value) === segmentationDetailId;
                return (
                  <button
                    key={`${option.label}-${option.value}`}
                    type="button"
                    onClick={() => onSelectSegmentation(Number(option.value))}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 border-b border-border/40 px-3 py-2 text-left text-sm last:border-b-0",
                      "hover:bg-muted/60",
                      selected && "bg-muted/50 font-medium"
                    )}
                  >
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {option.label}
                    </span>
                    <span className="truncate">{option.text}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function OverviewScopePicker({
  playerId,
  playerLabel,
  segmentationDetailId,
  onSelectPlayer,
  onSelectSegmentation,
  onClear,
  allowedSegmentationIds,
}: {
  playerId: number | null;
  playerLabel?: string | null;
  segmentationDetailId: number | null;
  onSelectPlayer: (playerId: number) => void;
  onSelectSegmentation: (segmentationDetailId: number) => void;
  onClear?: () => void;
  allowedSegmentationIds?: Set<number> | null;
}) {
  const [open, setOpen] = React.useState(false);
  const { segmentationOptions } = useSegmentation([...SEGMENT_TYPES]);

  const selectedSegmentation =
    segmentationOptions.find(option => Number(option.value) === segmentationDetailId) ?? null;

  const label =
    playerId != null
      ? playerLabel || "Selected Player"
      : selectedSegmentation
      ? selectedSegmentation.text
      : "Entire company";

  const hasScope = playerId != null || segmentationDetailId != null;
  const canClear = hasScope && onClear != null;
  const LabelIcon = playerId != null ? UserRound : selectedSegmentation ? MapPinned : Building2;

  const selectPlayer = (nextPlayerId: number) => {
    onSelectPlayer(nextPlayerId);
    setOpen(false);
  };

  const selectSegmentation = (segmentationDetailId: number) => {
    onSelectSegmentation(segmentationDetailId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="inline-flex max-w-full items-center">
        <Popover.Trigger
          render={
            <button
              type="button"
              className={cn(
                "group inline-flex max-w-full cursor-pointer items-center gap-2 rounded-lg border border-transparent",
                "px-2.5 py-1.5 text-sm font-semibold text-foreground",
                "transition-colors hover:border-border hover:bg-muted/50",
                hasScope && "border-border bg-muted/30",
                canClear && "rounded-r-none border-r-0"
              )}
            >
              <LabelIcon className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
              <span className="truncate">{label}</span>
              {!canClear && <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            </button>
          }
        />
        {canClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear scope"
            title="Clear"
            className={cn(
              "inline-flex cursor-pointer items-center self-stretch rounded-r-lg border border-border bg-muted/30 px-2",
              "text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            )}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <Popover.Content align="start" className="w-[22rem] gap-3 p-3 font-sans">
        <OverviewScopePickerContent
          enabled={open}
          playerId={playerId}
          segmentationDetailId={segmentationDetailId}
          onSelectPlayer={selectPlayer}
          onSelectSegmentation={selectSegmentation}
          allowedSegmentationIds={allowedSegmentationIds}
        />
      </Popover.Content>
    </Popover>
  );
}
